import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { getPnlByYear, getPnlByYearFiltered } from '../store/analytics/thunks';
import { getTradesOfDateFiltered } from '../store/trades/thunks';
import { getJournalEntries, getTradeReview } from '../store/journal/thunks';
import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
import TradesOfRangeDialog from "./pnlcalendar/TradesOfRangeDialog";
import ScreenshotModal from "./pnlcalendar/ScreenshotModal";
import JournalEntryDialog from "./pnlcalendar/JournalEntryDialog";
import PnlCalendarFilters from "./pnlcalendar/PnlCalendarFilters";
import MonthlyStatsRow from "./pnlcalendar/MonthlyStatsRow";
import { searchTicker } from '../store/trade';
import { useNavigate, useLocation } from "react-router-dom";
import monthsString from "../data/months";
import { BsJournalText } from "react-icons/bs";
import PnlCalendarSkeleton from './PnlCalendarSkeleton';

import '../styles/filter.css';
import '../styles/journal.css';

import {
  Flex,
  Center,
  Text,
  Icon,
  HStack,
  VStack,
  Button,
  useColorModeValue,
  useDisclosure,
  Select,
  Box,
  Grid,
  GridItem,
} from "@chakra-ui/react";


function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PnlCalendar({ user, pnlYTD }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();
  const { preferences } = useSelector((state) => state.user);
  const trades = useSelector((state) => state.trades.tradesOfDay);
  const showToast = useToastNotification();
  useAsyncState({
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });
  const hasPnLInfo = ((pnlYTD && Object.keys(pnlYTD).length > 0 && pnlYTD.months && Object.keys(pnlYTD.months).length > 0) ? (true):(false));
  const hasTradesofDay = ((trades && Object.keys(trades).length > 0 && trades.trades && Object.keys(trades.trades).length > 0) ? (true):(false));
  const hasStats = ((trades && Object.keys(trades).length > 0 && trades.stats && Object.keys(trades.stats).length > 0) ? (true):(false));
  const hasPreferences = ((preferences && Object.keys(preferences).length > 0) ? (true):(false));

  // Same pattern as Home: state.ui.loading flips false when the first concurrent
  // thunk resolves, so the "No PnL Info" empty branch can briefly flash before
  // pnlYTD lands. Gate the skeleton on per-slice "loaded" flags too.
  const pnlYTDLoaded = pnlYTD && Object.keys(pnlYTD).length > 0;
  const preferencesLoaded = preferences && Object.keys(preferences).length > 0;
  const initialDataLoaded = pnlYTDLoaded && preferencesLoaded;

  const [toggleFilter, setToggleFilter] = useState(false);

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  const [calDay, setCalDay] = useState(0);
  const [calStartWeek, setCalStartWeek] = useState(0);
  const [calEndWeek, setCalEndWeek] = useState(0);
  const [calMonth, setCalMonth] = useState(month);
  const [calYear, setCalYear] = useState(year);

  const { onClose } = useDisclosure();

  const [tradesOfDayPopUp, setTradesOfDayPopUp] = useState(false);
  const [tradesOfWeekPopUp, setTradesOfWeekPopUp] = useState(false);
  const [tradesOfMonthPopUp, setTradesOfMonthPopUp] = useState(false);

  const [journalEntryOfDayPopUp, setJournalEntryOfDayPopUp] = useState(false);
  const [dayReviews, setDayReviews] = useState({});
  const [screenshotModalUrl, setScreenshotModalUrl] = useState(null);

  const [filter_trade_type, setFilterTradeType] = useState("");
  const [filter_security_type, setFilterSecurityType] = useState("");
  const [filter_trade_direction, setFilterTradeDirection] = useState("");
  const [filter_ticker_name, setFilterTickerName] = useState("");

  const authLoading = useSelector((state) => state.ui.loading);

  const [searchTickerValue, setSearchTickerValue] = useState('');
  const [searchTickerResults, setSearchTickerResults] = useState([]);
  const [selectedTickerValue, setSelectedTickerValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [filters, setFilters] = useState({});

  // Color mode values
  const bgColor = useColorModeValue("gray.200", "gray.800");
  const cardBg = useColorModeValue("whiteAlpha.900", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headerBg = useColorModeValue("gray.100", "gray.700");
  const cellBorderColor = useColorModeValue("gray.300", "gray.600");
  const iconColor = useColorModeValue("black", "white");
  const positiveBg = useColorModeValue("green.200", "green.400");
  const negativeBg = useColorModeValue("red.200", "red.400");
  const neutralBg = useColorModeValue("gray.100", "gray.700");
  const neutralDayColor = useColorModeValue("black", "white");
  const reviewBg = useColorModeValue("blue.50", "gray.600");
  const reviewCardBg = useColorModeValue("white", "gray.700");
  const reviewNoteColor = useColorModeValue("gray.600", "gray.300");

  // Shared cell styling
  const cellProps = {
    border: '1px',
    borderColor: cellBorderColor,
    rounded: 'md',
    p: 1,
    minWidth: "120px",
    w: '100%',
    h: '100%',
  };

  const statCellProps = {
    ...cellProps,
    minWidth: '150px',
    maxWidth: '150px',
  };

  const filterLabelMap = {
    trade_type: 'Trade Type',
    security_type: 'Security Type',
    trade_direction: 'Direction',
    ticker_name: 'Ticker',
  };

  const handleFilterChange = (newFilter) => {
    query.set('filter', newFilter);
    navigate(`?${query.toString()}`);
  };

  function filtersToQueryString(filters) {
    const params = new URLSearchParams();
    for (const key in filters) {
      if (filters[key] !== '') {
        params.append(key, filters[key]);
      }
    }
    return params.toString();
  }

  useEffect(() => {
    const savedCalendarFilters = window.localStorage.getItem('CalendarFilters');
    if (savedCalendarFilters) {
      const CalendarFilters = JSON.parse(savedCalendarFilters);
      setFilterTradeType(CalendarFilters.trade_type || "");
      setFilterSecurityType(CalendarFilters.security_type || "");
      setFilterTradeDirection(CalendarFilters.trade_direction || "");
      setFilterTickerName(CalendarFilters.ticker_name || "");
      setSelectedTickerValue(CalendarFilters.ticker_name || "");
      setFilters(CalendarFilters);
    } else {
      setFilterTradeType("");
      setFilterSecurityType("");
      setFilterTradeDirection("");
      setFilterTickerName("");
      setSelectedTickerValue("");
      setSearchTickerValue("");
      setFilters({});
    }
  }, [pnlYTD]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      setIsLoading(true);
      const filter = {};
      if (searchTickerValue !== '') {
        filter.ticker_name = searchTickerValue;
      }
      const response = await dispatch(searchTicker({ filter }));
      const topResults = response.payload.tickers.map(f => [f.ticker_name]);
      setSearchTickerResults(topResults);
      setIsLoading(false);
    };

    if (searchTickerValue) {
      fetchSearchResults();
      setIsDropdownOpen(true);
    } else {
      setSearchTickerResults([]);
      setIsDropdownOpen(false);
    }
  }, [searchTickerValue]);

  const handleInputTickerFilterChange = (event) => {
    setSelectedTickerValue('');
    setSearchTickerValue(event.target.value);
    setFilterTickerName(event.target.value);
  };

  const handleInputTickerFIlterClick = (event) => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTickerSelection = (selection) => {
    const selectionString = selection[0];
    setSelectedTickerValue(selectionString);
    setFilterTickerName(selectionString);
    setIsDropdownOpen(false);
  };

  const searchTickerResultItems = searchTickerResults.map((result) => (
    <li key={result} onClick={() => handleTickerSelection(result)}>
      {result}
    </li>
  ));


  const handleRemoveFilter = async (key) => {
    const updated = { ...filters };
    delete updated[key];
    if (key === 'trade_type') setFilterTradeType('');
    if (key === 'security_type') setFilterSecurityType('');
    if (key === 'trade_direction') setFilterTradeDirection('');
    if (key === 'ticker_name') { setFilterTickerName(''); setSearchTickerValue(''); setSelectedTickerValue(''); }

    if (Object.keys(updated).length === 0) {
      window.localStorage.removeItem('CalendarFilters');
      let yearVal = calYear;
      await dispatch(getPnlByYear({ year: yearVal }));
      handleFilterChange(filtersToQueryString({}));
      setFilters({});
    } else {
      window.localStorage.setItem('CalendarFilters', JSON.stringify(updated));
      let yearVal = calYear;
      await dispatch(getPnlByYearFiltered({ filters: updated, year: yearVal }));
      handleFilterChange(filtersToQueryString(updated));
      setFilters(updated);
    }
  };

  const formatter = (currencyCode) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
    });
  };

  const percent = new Intl.NumberFormat('default', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const buildTradeFilters = () => {
    const f = {};
    if (filter_trade_type !== '') f.trade_type = filter_trade_type;
    if (filter_security_type !== '') f.security_type = filter_security_type;
    if (filter_trade_direction !== '') f.trade_direction = filter_trade_direction;
    if (filter_ticker_name !== '') f.ticker_name = filter_ticker_name;
    return f;
  };

  const handleTradesOfDay = async (e, cal_day) => {
    e.preventDefault();
    setTradesOfDayPopUp(true);
    setCalDay(cal_day);
    const cal_date = new Date(calYear, calMonth, cal_day);
    const tradeFilters = buildTradeFilters();
    tradeFilters.trade_date = cal_date.toISOString().split('T')[0];
    const tradesResult = await dispatch(getTradesOfDateFiltered({ filters: tradeFilters }));
    if (tradesResult.payload && tradesResult.payload.trades) {
      const reviewMap = {};
      for (const t of tradesResult.payload.trades) {
        const reviewResult = await dispatch(getTradeReview({ trade_id: t.trade_id }));
        if (reviewResult.payload && reviewResult.payload.trade_review_id) {
          reviewMap[t.trade_id] = reviewResult.payload;
        }
      }
      setDayReviews(reviewMap);
    }
  };

  const handleJournalEntryOfDay = async (e, cal_day) => {
    e.preventDefault();
    setJournalEntryOfDayPopUp(true);
    setCalDay(cal_day);
  };

  const handleGoToJournalEntry = async (e) => {
    e.preventDefault();
    const journal_date = new Date(calYear, calMonth, calDay);
    const date = journal_date.toISOString().split("T")[0];
    const journalInfo = {
      selectedDate: date,
      source: 'calendar',
      lastApiCall: new Date().toISOString()
    };
    window.localStorage.setItem('journalInfo', JSON.stringify(journalInfo));
    await dispatch(getJournalEntries({ date }));
    navigate("/journal");
  };

  function getWeekDateRange(year, month, week) {
    const firstDayOfMonth = new Date(year, month, 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    let startDate = new Date(year, month, 1 + (week - 1) * 7 - firstDayOfWeek);
    let endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
    if (startDate < firstDayOfMonth) {
      startDate = firstDayOfMonth;
    }
    const lastDayOfMonth = new Date(year, month + 1, 0);
    if (endDate > lastDayOfMonth) {
      endDate = lastDayOfMonth;
    }
    return { startDate, endDate };
  }

  const handleTradesOfWeek = async (e, week_index) => {
    e.preventDefault();
    setTradesOfWeekPopUp(true);
    const { startDate, endDate } = getWeekDateRange(calYear, calMonth, week_index);
    setCalStartWeek(startDate);
    setCalEndWeek(endDate);
    const tradeFilters = buildTradeFilters();
    tradeFilters.from_date = startDate.toISOString().split('T')[0];
    tradeFilters.to_date = endDate.toISOString().split('T')[0];
    await dispatch(getTradesOfDateFiltered({ filters: tradeFilters }));
  };

  const handleTradesOfMonth = async (e) => {
    e.preventDefault();
    setTradesOfMonthPopUp(true);
    let startDate = new Date(calYear, calMonth, 1);
    let endDate = new Date(calYear, calMonth + 1, 0);
    const tradeFilters = buildTradeFilters();
    tradeFilters.from_date = startDate.toISOString().split('T')[0];
    tradeFilters.to_date = endDate.toISOString().split('T')[0];
    await dispatch(getTradesOfDateFiltered({ filters: tradeFilters }));
  };

  const handleClosePopUp = () => {
    setTradesOfDayPopUp(false);
    setTradesOfWeekPopUp(false);
    setTradesOfMonthPopUp(false);
    setJournalEntryOfDayPopUp(false);
    onClose();
  };

  const colorChange = (pnl) => {
    if (pnl > 0) return positiveBg;
    if (pnl < 0) return negativeBg;
    return neutralBg;
  };

  const colorChangeDay = (pnl) => {
    if (pnl > 0) return "green.400";
    if (pnl < 0) return "red.400";
    return neutralDayColor;
  };

  const pnlValue = (pnl) => {
    if (pnl.includes("-")) {
      return "(".concat(pnl.substring(1), ")");
    } else {
      return pnl;
    }
  };

  const getDays = (yearr, monthh) => {
    return new Date(yearr, monthh, 0).getDate();
  };

  const getBlanksForFirstDay = () => {
    let content = [];
    let firstDay = new Date(calYear, calMonth, 1).getDay();
    for (let i = 0; i < firstDay; i++) {
      content.push(<GridItem key={`blank-start-${i}`} {...cellProps} bg={headerBg} />);
    }
    return content;
  };

  const getBlanksForEndOfMonth = () => {
    let content = [];
    let totalDays = getDays(calYear, calMonth + 1);
    let firstDay = new Date(calYear, calMonth, 1).getDay();
    let spotsLeft = 42 - totalDays - firstDay;
    for (let i = 0; i < spotsLeft; i++) {
      content.push(<GridItem key={`blank-end-${i}`} {...cellProps} bg={headerBg} />);
    }
    return content;
  };

  const getMonthlyTotal = () => {
    let monthlyTotal = 0;
    for (let i = 0; i < 31; i++) {
      monthlyTotal += pnlYTD.months[calMonth][calMonth][i]['pnl'];
    }
    return monthlyTotal;
  };

  const getGreenDayTotal = () => {
    let greenDayTotal = 0;
    for (let i = 0; i < 31; i++) {
      if (pnlYTD.months[calMonth][calMonth][i]['pnl'] > 0) {
        greenDayTotal += 1;
      }
    }
    return greenDayTotal;
  };

  const getRedDayTotal = () => {
    let redDayTotal = 0;
    for (let i = 0; i < 31; i++) {
      if (pnlYTD.months[calMonth][calMonth][i]['pnl'] < 0) {
        redDayTotal += 1;
      }
    }
    return redDayTotal;
  };

  const getGreenDayPercent = () => {
    let greenDays = 0;
    let totalTradeDays = 0;
    let totalDays = getDays(calYear, calMonth);
    for (let i = 0; i < totalDays; i++) {
      if (pnlYTD.months[calMonth][calMonth][i]['pnl'] > 0) greenDays += 1;
      if (pnlYTD.months[calMonth][calMonth][i]['count'] > 0) totalTradeDays += 1;
    }
    return totalTradeDays > 0 ? (greenDays / totalTradeDays) : 0;
  };

  const getRedDayPercent = () => {
    let redDays = 0;
    let totalTradeDays = 0;
    let totalDays = getDays(calYear, calMonth);
    for (let i = 0; i < totalDays; i++) {
      if (pnlYTD.months[calMonth][calMonth][i]['pnl'] < 0) redDays += 1;
      if (pnlYTD.months[calMonth][calMonth][i]['count'] > 0) totalTradeDays += 1;
    }
    return totalTradeDays > 0 ? (redDays / totalTradeDays) : 0;
  };

  const getBreakevenDayPercent = () => {
    let evenDays = 0;
    let totalTradeDays = 0;
    let totalDays = getDays(calYear, calMonth);
    for (let i = 0; i < totalDays; i++) {
      if (pnlYTD.months[calMonth][calMonth][i]['pnl'] === 0 && pnlYTD.months[calMonth][calMonth][i]['count'] > 0) evenDays += 1;
      if (pnlYTD.months[calMonth][calMonth][i]['count'] > 0) totalTradeDays += 1;
    }
    return totalTradeDays > 0 ? (evenDays / totalTradeDays) : 0;
  };

  const getWeeklyTotals = () => {
    let content = [];
    let weekTotals = [0.0, 0.0, 0.0, 0.0, 0.0, 0.0];
    let weekCounts = [0.0, 0.0, 0.0, 0.0, 0.0, 0.0];
    let weekCount = 0;
    let dayCount = 0;
    let pnlDayCount = 0;
    let firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const totalDays = firstDay + daysInMonth;
    const numberOfWeeks = Math.ceil(totalDays / 7);

    while (weekCount < numberOfWeeks) {
      if (dayCount !== 0 && dayCount % 7 === 0) {
        weekCount += 1;
      }
      if (weekCount === 0 && dayCount === 0 && firstDay !== 0) {
        dayCount += firstDay;
      }
      if (pnlYTD.months[calMonth][calMonth][pnlDayCount]?.pnl !== 0.0 && pnlDayCount < daysInMonth) {
        weekTotals[weekCount] += pnlYTD.months[calMonth][calMonth][pnlDayCount]?.pnl ?? 0;
      }
      if (pnlYTD.months[calMonth][calMonth][pnlDayCount]?.count !== 0.0 && pnlDayCount < daysInMonth) {
        weekCounts[weekCount] += pnlYTD.months[calMonth][calMonth][pnlDayCount]?.count ?? 0;
      }
      dayCount += 1;
      pnlDayCount += 1;
    }
    for (let i = 0; i < numberOfWeeks; i++) {
      content.push(
        <GridItem key={`week-${i}`} {...cellProps} _hover={{ bg: "gray.400" }} bg={colorChange(weekTotals[i])} onClick={e => handleTradesOfWeek(e, i + 1)} cursor="pointer">
          <Text fontWeight="bold">-</Text>
          <Center padding={1}>
            {weekCounts[i]} Trade(s)
          </Center>
          <Center fontWeight="bold">
            {pnlValue(formatter(preferences.preferred_currency).format(weekTotals[i]))}
          </Center>
        </GridItem>
      );
    }
    for (let i = numberOfWeeks; i < 6; i++) {
      content.push(<GridItem key={`week-empty-${i}`} {...cellProps} bg={neutralBg} />);
    }
    return content;
  };

  const getPnlDays = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const totalDays = firstDay + daysInMonth;
    const numberOfWeeks = Math.ceil(totalDays / 7);
    let weeks = Array.from({ length: numberOfWeeks }, () => []);

    for (let i = 0; i < daysInMonth; i++) {
      const dayOfWeek = (firstDay + i) % 7;
      const weekOfMonth = Math.floor((firstDay + i) / 7);
      weeks[weekOfMonth][dayOfWeek] = i + 1;
    }

    let content = [];
    weeks.forEach((week, weekIndex) => {
      week.forEach((day, dayIndex) => {
        if (day) {
          const pnl = pnlYTD.months[calMonth][calMonth][day - 1];
          content.push(
            <GridItem
              key={`${weekIndex}-${dayIndex}`}
              {...cellProps}
              _hover={{ bg: "gray.400" }}
              bg={colorChange(pnl['pnl'])}
              onClick={e => handleTradesOfDay(e, day)}
              cursor="pointer"
            >
              <HStack spacing={0} p={0} w="100%" justify="space-between" align="center" paddingStart={1} paddingEnd={1}>
                <Text fontWeight="bold">{day}</Text>
                {pnl['journal_exists'] === true && (
                  <Button p={0} minW="auto" h="auto" colorScheme="white" onClick={(e) => {
                    e.stopPropagation();
                    handleJournalEntryOfDay(e, day);
                  }}>
                    <Icon as={BsJournalText} color={iconColor} size="lg" _hover={{ color: "gray.500" }} />
                  </Button>
                )}
              </HStack>
              <Center padding={1}>
                {pnl['count']} Trade(s)
              </Center>
              <Center fontWeight="bold">
                {pnlValue(formatter(preferences.preferred_currency).format(pnl['pnl']))}
              </Center>
            </GridItem>
          );
        }
      });
    });

    return content;
  };

  function loadFiltersFromLocalStorage(page) {
    const filters = localStorage.getItem(page + 'Filters');
    return filters ? JSON.parse(filters) : {};
  }

  useEffect(() => {
    let yearVal = calYear;
    let filters = loadFiltersFromLocalStorage('Calendar');
    const hasFilters = Object.keys(filters).length > 0;
    if (user.user_id != undefined) {
      if (hasFilters) {
        dispatch(getPnlByYearFiltered({ filters, year: yearVal }));
      } else {
        dispatch(getPnlByYear({ year: yearVal }));
      }
    }
  }, [calYear, user]);

  const getFilterComponent = () => (
    <PnlCalendarFilters
      filters={filters}
      onRemoveFilter={handleRemoveFilter}
      labelMap={filterLabelMap}
      toggleFilter={toggleFilter}
      onToggleFilter={() => setToggleFilter(!toggleFilter)}
      filter_trade_type={filter_trade_type}
      setFilterTradeType={setFilterTradeType}
      filter_security_type={filter_security_type}
      setFilterSecurityType={setFilterSecurityType}
      filter_trade_direction={filter_trade_direction}
      setFilterTradeDirection={setFilterTradeDirection}
      searchTickerValue={searchTickerValue}
      selectedTickerValue={selectedTickerValue}
      onTickerInputChange={handleInputTickerFilterChange}
      onTickerInputClick={handleInputTickerFIlterClick}
      isDropdownOpen={isDropdownOpen}
      isLoading={isLoading}
      searchTickerResultItems={searchTickerResultItems}
      onSubmit={handleSubmitFilter}
      onClear={handleClearFilter}
    />
  );

  const handleSubmitFilter = async (e) => {
    e.preventDefault();
    setToggleFilter(false);
    const filters = {};
    if (filter_trade_type !== '') filters.trade_type = filter_trade_type;
    if (filter_security_type !== '') filters.security_type = filter_security_type;
    if (filter_trade_direction !== '') filters.trade_direction = filter_trade_direction;
    if (filter_ticker_name !== '') filters.ticker_name = filter_ticker_name;
    let yearVal = calYear;
    window.localStorage.setItem('CalendarFilters', JSON.stringify(filters));
    await dispatch(getPnlByYearFiltered({ filters, year: yearVal }));
    handleFilterChange(filtersToQueryString(filters));
    setFilters(filters);
  };

  const handleClearFilter = async (e) => {
    setToggleFilter(false);
    setFilterTradeType('');
    setFilterSecurityType('');
    setFilterTradeDirection('');
    setFilterTickerName('');
    setSearchTickerValue('');
    setSelectedTickerValue('');
    window.localStorage.removeItem('CalendarFilters');
    setFilters({});
    let yearVal = calYear;
    await dispatch(getPnlByYear({ year: yearVal }));
    handleFilterChange(filtersToQueryString({}));
  };

  const anyPopupOpen = tradesOfDayPopUp || tradesOfWeekPopUp || tradesOfMonthPopUp || journalEntryOfDayPopUp;

  return (
    <Flex flexDirection="column" minHeight="100vh" backgroundColor={bgColor}>
      <Box flex="auto" overflowX="auto" overflowY="auto" m={2}>
        {(authLoading || !initialDataLoaded) && !anyPopupOpen ? (
          <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={cardBg} boxShadow="md">
            <PnlCalendarSkeleton />
          </VStack>
        ) : (
          <VStack flex="auto" p={4} spacing={4} w="full" align="stretch" backgroundColor={cardBg} boxShadow="md">
            {getFilterComponent()}

            {hasPnLInfo && hasPreferences ? (
              <>
                {/* Month/Year Selectors */}
                <HStack mx="auto">
                  <Select
                    w="150px"
                    border="1px"
                    borderColor={cellBorderColor}
                    size="md"
                    variant="filled"
                    value={monthsString[calMonth]}
                    onChange={(e) => setCalMonth(monthsString.indexOf(e.target.value))}
                  >
                    {monthsString.map((m) => (<option key={m}>{m}</option>))}
                  </Select>
                  <Select
                    w="100px"
                    size="md"
                    variant="filled"
                    border="1px"
                    borderColor={cellBorderColor}
                    value={calYear}
                    onChange={(e) => setCalYear(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 16 }, (_, i) => year - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Select>
                </HStack>

                {/* Monthly Stats */}
                <MonthlyStatsRow
                  statCellProps={statCellProps}
                  headerBg={headerBg}
                  monthlyPnlBg={colorChange(getMonthlyTotal())}
                  monthlyPnlValue={pnlValue(formatter(preferences.preferred_currency).format(getMonthlyTotal()))}
                  greenDayCount={getGreenDayTotal()}
                  redDayCount={getRedDayTotal()}
                  greenDayPercent={getGreenDayPercent()}
                  redDayPercent={getRedDayPercent()}
                  breakEvenPercent={getBreakevenDayPercent()}
                  onMonthlyPnlClick={handleTradesOfMonth}
                  formatPercent={(v) => percent.format(v)}
                />

                {/* Calendar Grid + Weekly Totals */}
                <HStack spacing={4} align="start" mx="auto">
                  <VStack spacing={3} align="stretch">
                    {/* Day-of-week headers */}
                    <Grid templateColumns="repeat(7, 1fr)" gap={2}>
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                        <GridItem key={day} {...cellProps} bg={headerBg}>
                          <Center fontWeight="bold" fontSize="sm">{day}</Center>
                        </GridItem>
                      ))}
                    </Grid>
                    {/* Calendar days */}
                    <Grid templateColumns="repeat(7, 1fr)" templateRows="repeat(6, 1fr)" gap={2}>
                      {getBlanksForFirstDay()}
                      {getPnlDays()}
                      {getBlanksForEndOfMonth()}
                    </Grid>
                  </VStack>

                  {/* Weekly Totals */}
                  <VStack spacing={3} align="stretch">
                    <GridItem {...cellProps} bg={headerBg} whiteSpace="nowrap">
                      <Center fontWeight="bold" fontSize="sm">Weekly Totals</Center>
                    </GridItem>
                    <Grid templateColumns="repeat(1, 1fr)" templateRows="repeat(6, 1fr)" gap={2}>
                      {getWeeklyTotals()}
                    </Grid>
                  </VStack>
                </HStack>
              </>
            ) : (
              <Center py={10}>
                <Text color={textColor} fontSize="sm">No PnL Info</Text>
              </Center>
            )}
          </VStack>
        )}
      </Box>

      <TradesOfRangeDialog
        isOpen={tradesOfDayPopUp}
        onClose={handleClosePopUp}
        isLoading={authLoading}
        headerText={`Trades Closed on ${(new Date(calYear, calMonth, calDay)).toDateString()}`}
        emptyText="No Trades Closed on This Day"
        hasTrades={hasTradesofDay}
        trades={trades?.trades}
        totalTrades={pnlYTD.months?.[calMonth]?.[calMonth]?.[calDay - 1]?.count ?? 0}
        totalPnl={pnlYTD.months?.[calMonth]?.[calMonth]?.[calDay - 1]?.pnl ?? 0}
        reviews={dayReviews}
        onScreenshotClick={setScreenshotModalUrl}
        pnlValue={pnlValue}
        formatter={formatter}
        colorChange={colorChangeDay}
        preferences={preferences}
        reviewBg={reviewBg}
        reviewCardBg={reviewCardBg}
        reviewNoteColor={reviewNoteColor}
      />

      <JournalEntryDialog
        isOpen={journalEntryOfDayPopUp}
        onClose={handleClosePopUp}
        isLoading={authLoading}
        dateLabel={(new Date(calYear, calMonth, calDay)).toDateString()}
        entryText={pnlYTD.months?.[calMonth]?.[calMonth]?.[calDay - 1]?.entrytext}
        onGoToEdit={handleGoToJournalEntry}
      />

      <TradesOfRangeDialog
        isOpen={tradesOfWeekPopUp}
        onClose={handleClosePopUp}
        isLoading={authLoading}
        headerText={`Trades Closed from ${(new Date(calStartWeek)).toDateString()} to ${(new Date(calEndWeek)).toDateString()}`}
        emptyText="No Trades Closed in This Week"
        hasTrades={hasTradesofDay && hasStats}
        trades={trades?.trades}
        totalTrades={trades?.stats?.num_trades ?? 0}
        totalPnl={trades?.stats?.total_pnl ?? 0}
        pnlValue={pnlValue}
        formatter={formatter}
        colorChange={colorChangeDay}
        preferences={preferences}
      />

      <TradesOfRangeDialog
        isOpen={tradesOfMonthPopUp}
        onClose={handleClosePopUp}
        isLoading={authLoading}
        headerText={`Trades Closed in ${monthsString[calMonth]}`}
        emptyText="No Trades Closed in This Month"
        hasTrades={hasTradesofDay && hasStats}
        trades={trades?.trades}
        totalTrades={trades?.stats?.num_trades ?? 0}
        totalPnl={trades?.stats?.total_pnl ?? 0}
        pnlValue={pnlValue}
        formatter={formatter}
        colorChange={colorChangeDay}
        preferences={preferences}
      />

      <ScreenshotModal
        url={screenshotModalUrl}
        onClose={() => setScreenshotModalUrl(null)}
      />
    </Flex>
  );
}
